import axios from 'axios';
import { Lock, Mail } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Seo from '../../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success(isLogin ? 'Login successful!' : 'Registration successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-6" data-testid="admin-login-page">
      <Seo title="Admin Portal | YTEK Controls" description="Administrative area." noindex />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/images/ytek-logo.png"
            alt="Ytek Controls"
            className="h-16 w-auto mx-auto mb-4"
          />
          <h1 className="font-heading text-3xl font-medium text-white mb-2">Admin Portal</h1>
          <p className="text-slate-400">
            {isLogin ? 'Sign in to manage your content' : 'Create your admin account'}
          </p>
        </div>

        <div className="bg-slate-900 border border-white/10 p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  data-testid="admin-input-fullname"
                  className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 transition-colors outline-none"
                  placeholder="Enter your name"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="admin-input-email"
                  className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 pl-12 pr-4 text-white placeholder:text-slate-500 transition-colors outline-none"
                  placeholder="admin@ytekcontrols.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="admin-input-password"
                  className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 pl-12 pr-4 text-white placeholder:text-slate-500 transition-colors outline-none"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-submit-button"
              className="w-full px-8 py-4 bg-ocean-500 hover:bg-ocean-600 text-white font-medium uppercase tracking-widest text-xs transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="admin-toggle-mode"
              className="text-sm text-slate-400 hover:text-ocean-500 transition-colors"
            >
              {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign In'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
